import classnames from "classnames";
import { flatMap } from "lodash";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import { useNavigate } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import OrangeTimerSVG from "@krea/customer-features/images/svg/orange-timer.svg";
import { settings } from "@krea/customer-features/settings";
import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import NavigateAction from "@krea/customer-features/shared-components/navigate-action";
import { Text } from "@krea/customer-features/shared-components/text";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useGetCachedLenderResponses } from "@krea/customer-features/store/lender-responses/hooks/useGetCachedLenderResponses";
import {
  LoanApplicationStates,
  LoanApplicationVerificationFailureReasons,
} from "@krea/customer-features/types/loanApplication";
import { APPLICATION_NAME } from "@krea/customer-features/utils";

import LoanApplicationMessages from "../loan-application-messages";

const BidsHeader = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const navigate = useNavigate();

  const { data } = useGetCachedCurrentLoanApplication();
  const {
    id: loanApplicationID,
    state,
    verificationFailureReasons,
  } = data ?? {};

  const { data: lenderResponsesData = {} } =
    useGetCachedLenderResponses(loanApplicationID);

  const bidsCount = flatMap(lenderResponsesData).length;
  const hasBids = bidsCount > 0;

  const isCustomerWeb = settings.appName === APPLICATION_NAME.CUSTOMER_WEB;
  const isPartnerWeb = settings.appName === APPLICATION_NAME.PARTNER_WEB;

  const getTitle = () => {
    if (
      state &&
      ![LoanApplicationStates.CREATED, LoanApplicationStates.REJECTED].includes(
        state,
      )
    ) {
      return null;
    }

    if (state === LoanApplicationStates.CREATED) {
      return t(`dash.createdState.heading`);
    }

    return t(`dash.rejectedState.heading`);
  };

  const renderReasonAccordionItem = () => {
    if (
      state &&
      ![LoanApplicationStates.CREATED, LoanApplicationStates.REJECTED].includes(
        state,
      )
    ) {
      return null;
    }

    // If it's in created due to a duplicate
    if (
      verificationFailureReasons?.includes(
        LoanApplicationVerificationFailureReasons.DUPLICATE_EXISTS,
      )
    ) {
      return null;
    }

    const textType =
      state === LoanApplicationStates.CREATED
        ? "applicationNotSubmitted"
        : "rejectedByKrea";

    return (
      <AccordionItem
        id="ps-text"
        className="tw-mt-4"
        title={t("dash.messages.applicationNotSubmitted.reason")}
      >
        <Text>{t(`dash.messages.${textType}.description`)}</Text>
      </AccordionItem>
    );
  };

  return (
    <div>
      {state === LoanApplicationStates.CREATED ||
      state === LoanApplicationStates.REJECTED ? (
        <div
          className={classnames({
            "lg:tw-flex lg:tw-justify-between":
              state === LoanApplicationStates.CREATED ||
              state === LoanApplicationStates.REJECTED,
          })}
        >
          {isMobile ? <h3>{getTitle()}</h3> : <h1>{getTitle()}</h1>}

          <div
            className={classnames({
              "tw-mb-3 tw-inline-block lg:tw-mb-0":
                state === LoanApplicationStates.CREATED ||
                state === LoanApplicationStates.REJECTED,
            })}
          >
            <div className="tw-flex tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-py-2 tw-pl-1 tw-pr-2">
              <SVG
                className="tw-h-[18px] md:tw-mr-px md:tw-mt-px md:tw-h-[20px]"
                src={OrangeTimerSVG}
              />
              <p className="tw-m-0 tw-text-xs md:tw-text-sm">
                {t("dash.applicationNotSubmitted")}
              </p>
            </div>
          </div>
        </div>
      ) : isMobile ? (
        <h3>{getTitle()}</h3>
      ) : (
        <h1>{getTitle()}</h1>
      )}

      <div className="tw-flex tw-w-full tw-flex-col tw-flex-wrap tw-align-middle xl:tw-flex-row">
        <Text className="tw-mb-4 tw-w-full xl:tw-mb-2">
          <LoanApplicationMessages
            hasBids={hasBids}
            isSubmitApplicationModal={false}
          />

          {renderReasonAccordionItem()}

          {state === LoanApplicationStates.CREATED && (
            <NavigateAction
              className="tw-mt-4"
              title={t("dash.changeInformationForApplication.title")}
              button={{
                text: t("dash.changeInformationForApplication.back"),
                onClick: () => {
                  if (isCustomerWeb) {
                    navigate("/direct/improve-application-questions/preview");
                  } else if (isPartnerWeb) {
                    navigate(`/loan-applications/${loanApplicationID}/preview`);
                  }
                },
              }}
            />
          )}
        </Text>
      </div>
    </div>
  );
};

export default BidsHeader;
